import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import {
  AttentionSeverityPlate,
  getAttentionStatusRole,
} from 'partner360/entities/attentionStatuses'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import type {
  IAttentionOwnerAssignment,
  IAttentionPartnerOverview,
  IAttentionReminderList,
  IAttentionReminderRoute,
} from '../../../../lib'
import { selectCockpitUserAccess } from '../../../../model/selectors'
import type { IAttentionPartnerOwners } from '../../../../model/selectors/selectCockpitAttentionLevel'
import {
  useAttentionPendingPbmReplacements,
  useCanReplaceAttentionPbm,
} from '../../hooks/useAttentionPbmReplacement'
import { useAttentionStatusesText } from '../../hooks/useAttentionStatusesText'
import { AttentionPartnerLink } from '../attentionPartnerLink'
import { AttentionPbmName, AttentionPbmReplacePopup } from '../attentionPbmReplacement'
import { AttentionReminderButton } from '../attentionReminderButton'

import styles from './AttentionStatusesFullscreen.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.fullscreen

/* No status is picked on a partner's card, so its people get the summary of everything it holds open. */
const PBM_SUMMARY_ROUTE: IAttentionReminderRoute = { bitValue: null, actionId: 'remindPbm' }
const MANAGER_SUMMARY_ROUTE: IAttentionReminderRoute = { bitValue: null, actionId: 'remindManager' }

interface IAttentionFullscreenPartnerCardProps {
  overview: IAttentionPartnerOverview
  owners?: IAttentionPartnerOwners
}

const OwnerList = ({
  titleKey,
  owners,
  partner,
  route,
  list,
}: {
  titleKey: string
  owners: IAttentionOwnerAssignment[]
  /** Who the owners are reminded as, about every status the partner holds open. */
  route: IAttentionReminderRoute
  list: IAttentionReminderList
  /** Set where the owners are PBMs a manager may replace for this partner. */
  partner?: { partnerId: string; partnerName: string }
}) => {
  const canReplace = useCanReplaceAttentionPbm() && Boolean(partner)
  const getPending = useAttentionPendingPbmReplacements(
    partner ? [partner.partnerId] : [],
    canReplace
  )

  return (
    <section className={styles.panel}>
      <h4 className={styles.panelTitle}>
        <Translation span text={titleKey} />
      </h4>
      {owners.map(({ ownerId, ownerName }) => {
        const pending = partner ? getPending(partner.partnerId, ownerId) : undefined

        return (
          <div key={ownerId} className={`${styles.owner} ${styles.ownerSpread}`}>
            <AttentionPbmName name={ownerName} pending={pending} className={styles.ownerName} />
            <span className={styles.ownerActions}>
              {partner && canReplace && !pending && (
                <AttentionPbmReplacePopup
                  pbm={{ email: ownerId, name: ownerName }}
                  partners={[partner]}
                  assignedEmails={owners.map(({ ownerId: email }) => email)}
                />
              )}
              <AttentionReminderButton
                reminder={{ email: ownerId, name: ownerName }}
                route={route}
                list={list}
                filled
              />
            </span>
          </div>
        )
      })}
    </section>
  )
}

/** One partner with every status it holds open and the people who can close them. */
export const AttentionFullscreenPartnerCard = ({
  overview,
  owners,
}: IAttentionFullscreenPartnerCardProps) => {
  const text = useAttentionStatusesText()
  const { partner, open } = overview
  const { role } = useSelector(selectCockpitUserAccess)
  const statusRole = getAttentionStatusRole(role)
  const remindList = useMemo(
    () => ({
      partners: [{ partnerId: partner.partnerId, statuses: open.map(({ bitValue }) => bitValue) }],
    }),
    [open, partner.partnerId]
  )

  return (
    <div className={styles.overview}>
      <div className={styles.toolbar}>
        <span className={styles.scopeName}>{partner.partnerName}</span>
        <span className={styles.scopeSub}>{partner.country}</span>
        <span className={styles.toolbarEnd}>
          <AttentionPartnerLink
            variant="text"
            partnerId={partner.partnerId}
            partnerName={partner.partnerName}
            className={styles.planButton}
          />
        </span>
      </div>

      <div className={styles.panels}>
        <OwnerList
          titleKey={KEYS.columns.pbm}
          owners={owners?.pbms ?? []}
          partner={partner}
          route={PBM_SUMMARY_ROUTE}
          list={remindList}
        />
        <OwnerList
          titleKey={KEYS.columns.manager}
          owners={owners?.managers ?? []}
          route={MANAGER_SUMMARY_ROUTE}
          list={remindList}
        />
      </div>

      {open.length === 0 ? (
        <p className={styles.empty}>
          <Translation span text={KEYS.allClear} />
        </p>
      ) : (
        <div className={styles.statusGrid}>
          {open.map((status) => (
            <div key={status.bitValue} className={styles.panelStatus}>
              <AttentionSeverityPlate severity={status.severity} className={styles.panelPlate}>
                !
              </AttentionSeverityPlate>
              <span className={styles.panelStatusText}>
                <span>{text.statusTitleParts(status, 1).text}</span>
                <span className={styles.panelStatusSeverity}>
                  <Translation span {...text.phrase.severityName(status.severity)} />
                </span>
              </span>
              <span className={styles.panelStatusAction}>
                <Translation span {...text.phrase.actionLabel(status.bitValue, statusRole)} />
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

AttentionFullscreenPartnerCard.displayName = 'AttentionFullscreenPartnerCard'
